/* eslint-disable max-lines */

import { MENUS, PERMITTED_WHITELIST, MENU_SLUG } from '../constants/menu';
import { i18n } from '../i18n/translate';

const commonRolesPerCategory = {
  executive: ['executive'],
  engage_managers: ['event_manager', 'event_staff', 'workshop_admin', 'workshop_organizer'],
  next_managers: ['at_work_manager', 'at_work_advisor', 'pii_restricted_next_primary', 'pii_restricted_next_advisor'],
  next_main_managers: ['at_work_manager', 'pii_restricted_next_primary'],
  advisors: ['at_work_advisor', 'pii_restricted_next_advisor'],
  k12: ['administrator_executive', 'administrator_curriculum_coordinator', 'instructor_teacher', 'instructor_aide'],
  he_cc_fs_learner_names: ['he_learner', 'cc_learner', 'fac_staff_learner'],
  he_cc_fs_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin'],
  he_cc_fs_scorm_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin', 'scorm_manager'],
  he_cc_fs_primary_managers: ['he_admin', 'cc_admin', 'fac_staff_admin'],
};

const COMMON_VALS = Object.entries(commonRolesPerCategory).map(([key, value]) => ({ [key]: value }));

const menuRoles = {
  impact: commonRolesPerCategory.executive,
  engage: commonRolesPerCategory.engage_managers,
  promote: commonRolesPerCategory.next_managers,
  learn: commonRolesPerCategory.he_cc_fs_learner_names,
  train: commonRolesPerCategory.he_cc_fs_scorm_manager_names,
};

/*
  Some menu items have permissions to filter who should see it. If it's not there, everyone can see it.
  permission conditionals:
    has_capabilities, not_capabilities
    has_rule_set_role, not_rule_set_role
    has_rule_set, not_rule_set

  The && and || conditions in the menu entries are handled by the following structure:
    {
      has_capabilities: ['Capabilities::Something'], << OR
      has_rule_set: ['advisors', { 'engage_managers': 'event_manager' }]
    },{ << OR
      has_capabilities: ['Capabilities::Something'],
      has_rule_set_role: ['he_cc_fs_primary_managers']
    },{ << OR
      has_capabilities: ['Capabilities::Something', 'Capabilities::SomethingElse'],
      has_rule_set_role: ['next_managers', 'he_cc_fs_primary_managers']              ^ AND
    }
  ]
  TO put it another way:
  A conditional with ||
  [ { 'this' }, { 'or this' } ]
  A conditional with &&
  [ { 'this', 'and this' } , << OR { 'other stuff' } ]
  Combine these different ways to create complex conditions


  NOTE: When adding permissions to a menu object, has_capabilities: [] must come first in the order.

  Order is important when setting up the rest of the permissions as well. It
  works in the same way as an if statement with regard to order, with the first entry in the
  permissions object being the first item whose condition is checked. && and || setups work
  with the same "order of operations" one would expect when creating an "if" set.
  i. e.
  if( (capabilites === 'Capabilities::Something' || capabilities === 'Capabilities::SomethingElse') && role === next_managers.at_work_manager )

  has_capabilities will almost always be an && before moving on to whatever else we want to check. If it's missing, then it's assumed all capabilities will be allowed.

  If you have more than one Capabilities::whatever in the array, then it'll do || between them, then move on with &&.

  The reason we check has_capabilites first; if they don't have it, there's no need to check the rest of that block.
  However, you can chain blocks with different capabilites, each object block starting with its own has_capabilities.
*/

export class PermissionManager {
  constructor(user, organization, currentRuleSetName) {
    this.user = user;
    this.organization = organization;
    this.currentRuleSetName = currentRuleSetName;
  }

  adminHasPermission = value => {
    if (this.user?.all_access) {
      return true;
    } else if (this.user?.permissions && Array.isArray(this.user.permissions)) {
      return this.user.permissions.some(permission => value.includes(permission));
    }
    return false;
  };

  hasRuleSetRoleNameMatch = (ruleSetRoles, type, name) => {
    return ruleSetRoles.some(rule => {
      return type === 'has_rule_set_role' // headerMenu doesn't have currentRuleSetName, sidebarMenu does
        ? rule.rule_set === name &&
            (!this.currentRuleSetName || this.currentRuleSetName === name) &&
            rule.role === 'primary'
        : rule.rule_set !== name && rule.role !== 'primary';
    });
  };

  hasRuleSetNameMatch = (ruleSets, name, isAppSwitcher) => {
    // for App Switcher, we don't want to hide non-matching options, in order to be available to change rule set
    const selectedRuleSetMatches = isAppSwitcher || !this.currentRuleSetName || this.currentRuleSetName === name;

    return ruleSets.some(item => item.name === name && selectedRuleSetMatches);
  };

  hasNameMatch = (type, name, isAppSwitcher) => {
    if (!this.user) return false;
    const { permissions, rule_sets, user_rule_set_roles } = this.user;

    if (user_rule_set_roles && (type === 'has_rule_set_role' || type === 'not_rule_set_role')) {
      return this.hasRuleSetRoleNameMatch(user_rule_set_roles, type, name);
    }
    if (rule_sets && (type === 'has_rule_set' || type === 'not_rule_set')) {
      return this.hasRuleSetNameMatch(rule_sets, name, isAppSwitcher);
    }
    if (permissions) {
      return this.adminHasPermission(name);
    }
  };

  searchCommonVals = (type, key, value, isAppSwitcher) => {
    // value is an optional restrictive search against values per key in COMMON_VALS
    let checked = false;
    COMMON_VALS.forEach(entry => {
      if (entry[key]) {
        if (value) {
          checked = this.hasNameMatch(type, value, isAppSwitcher);
        } else {
          checked = entry[key].some(name => this.hasNameMatch(type, name, isAppSwitcher));
        }
      }
    });
    return checked;
  };

  hasCapability = value => {
    if (!this.organization) return false;
    return value.some(val => this.organization?.capability_list.includes(val));
  };

  hasSetOrRole = (key, value, isAppSwitcher) => {
    value = Array.isArray(value) ? value : [value];
    let access = false;
    value.every(val => {
      // val is a key in COMMON_VALS
      if ((key === 'has_rule_set' || key === 'not_rule_set') && val === 'event_manager_not_cra_content_only') {
        access = this.searchCommonVals(key, 'engage_managers', 'event_manager', isAppSwitcher);
      } else if ((key === 'has_rule_set' || key === 'not_rule_set') && typeof val === 'object') {
        // used for restricting to one value per key in COMMON_VALS search. i. e. { 'next_managers': 'at_work_manager' }
        access = this.searchCommonVals(key, Object.keys(val)[0], val[Object.keys(val)[0]], isAppSwitcher);
      } else if (key !== 'url' || key !== 'path_includes') {
        // catch has_ & not_ rule_set_role/rule_set coupled with a key for COMMON_VALS
        access = this.searchCommonVals(key, val, null, isAppSwitcher);
      }
      return !access;
    });
    return access;
  };

  processRulesConditionals = (key, value, isAppSwitcher) => {
    const hasAccess = this.hasSetOrRole(key, value, isAppSwitcher);
    if (key === 'not_rule_set' || key === 'not_rule_set_role' || key === 'not_capabilities') {
      return !hasAccess;
    }
    return hasAccess;
  };

  processCapabilitiesConditionals = (key, value) => {
    const hasAccess = this.hasCapability(value);
    if (key === 'not_capabilities') {
      return !hasAccess;
    }
    return hasAccess;
  };

  hasAccess = (permissions, isAppSwitcher) => {
    let hasAccess = true;
    // When we loop, we first do the && pass, and if false, we do another loop on permissions.every = an || pass.
    permissions.every(permission => {
      hasAccess = true; // set it to true for ||
      Object.entries(permission).forEach(([key, value]) => {
        if (key === 'has_capabilities' || key === 'not_capabilities')
          hasAccess = this.processCapabilitiesConditionals(key, value);
        if ((hasAccess && key === 'has_rule_set_role') || (hasAccess && key === 'not_rule_set_role'))
          hasAccess = this.processRulesConditionals(key, value, isAppSwitcher);
        if ((hasAccess && key === 'has_rule_set') || (hasAccess && key === 'not_rule_set'))
          hasAccess = this.processRulesConditionals(key, value, isAppSwitcher);
        if (hasAccess && key === 'path_includes')
          hasAccess = typeof window !== 'undefined' && window.location.href.indexOf(value) !== -1;
        if (hasAccess && key === 'user_all_access') hasAccess = this.user?.all_access;
        if (hasAccess && key === 'admin_has_permission') hasAccess = this.adminHasPermission(value);
        if (hasAccess && key === 'k12_visit_requestable')
          hasAccess = this.user?.current_school?.k12_requestable_curricula_for_teacher;
      });
      // break out of the loop if they have access
      return !hasAccess;
    });
    return hasAccess;
  };

  isAdvisor = () => {
    return this.user?.rule_sets ? this.user.rule_sets.some(rule => rule.role?.includes('advisor')) : false;
  };
}

const replaceSchoolName = (user, title) => {
  if (user && title.includes('{{current_school.name}}')) {
    return title.replace('{{current_school.name}}', user.current_school?.name || i18n.t('Choose a School'));
  }

  return title;
};

const replaceSchoolModalUrl = (user, target_url) => {
  if (user && target_url.includes('school_modal')) {
    return target_url.replace('school_modal', '');
  }

  return target_url;
};

const replaceUserName = (user, title) => {
  if (user && title.includes('{{user_name}}')) {
    return title.replace('{{user_name}}', user.full_name || `${user.first_name} ${user.last_name}`);
  }

  return title;
};

const replaceAccountManagerEmailText = (user, sub_text) => {
  if (user?.support_email && sub_text) {
    return sub_text.replace('{{support_email}}', user.support_email);
  }

  return sub_text;
};

const replaceAccountManagerEmailUrl = (user, target_url) => {
  if (user?.support_email && target_url) {
    return target_url.replace('{{support_email}}', user.support_email);
  }

  return target_url;
};

const replaceHelpUrl = (menu, organization, isAdvisor) => {
  if (isAdvisor && organization?.advisor_resource_url) {
    return organization.advisor_resource_url;
  } else if (menu?.foundryKnowledgeBaseUrl) {
    // foundryKnowledgeBaseUrl is ENV.FOUNDARY_KNOWLEDGE_BASE_URL if was added to this menu item
    return menu.foundryKnowledgeBaseUrl;
  }
  // the fallback is whatever the target_url is already set to in the menu file
  return menu.target_url;
};

const replaceMyProfileUrlContext = (targetUrl, isManager) => {
  return targetUrl.replace('{{context}}', isManager ? 'cportal' : 'student');
};

const replaceOrgSlug = (menu, organization, user) => {
  const orgSlug = organization ? organization.slug : user?.organization_slug;
  if (orgSlug && menu.target_url.includes('{{slug}}')) {
    return menu.target_url.replace('{{slug}}', orgSlug);
  }

  return menu.target_url;
};

const replacePrivacyUrl = (menu, organization) => {
  const privacyType = `privacy_${menu.privacy_processing}_url`;
  menu.target_url = organization && organization[privacyType] ? organization[privacyType] : '';
  return menu.target_url;
};

const addCurrentRuleSetName = (targetUrl, currentRuleSetName) => {
  if (!currentRuleSetName) return targetUrl;

  try {
    const url = new URL(targetUrl);
    if (!url.searchParams.get('current_rule_set_name')) {
      url.searchParams.set('current_rule_set_name', currentRuleSetName);
    }
    return url.toString();
  } catch (e) {
    console.error(`failed to add rule set to ${targetUrl}`, e);
    /*
      getting some undefined/cportal/something or other instead of a http://address
      here and there. breaks things for an advisor type. Reason: no next_url in the
      env.local file.
    */
  }
};

const dynamicSubdomainModules = ['engage', 'next'];
const domainPorts = [80, 443];

const withDynamicSubdomain = (targetUrl, organization) => {
  const url = new URL(targetUrl);

  let host = url.host;
  if (url.port && domainPorts.includes(url.port)) {
    host = `${host}:${url.port}`;
  }

  let domain = host.concat(url.pathname);
  if (organization?.slug) {
    domain = `${organization.slug}.${domain}`;
  }
  if (!organization) {
    // if no org, we are in Next Admin side of things.
    domain = `admin.${domain}`;
    if (url?.search) {
      // for content list and layouts, we need to maintain this part of the url building.
      domain = domain.concat(url.search);
    }
  }

  return `${url.protocol}//${domain}`;
};

const hasItems = target => {
  return Array.isArray(target) && target?.length;
};

const handleTargetUrlArrayValidation = (targets, permissionManager) => {
  for (let index = 0; index < targets.length; index++) {
    const { url, ...permissions } = targets[index];
    if (!Object.keys(permissions).length || permissionManager.hasAccess([permissions])) {
      return url;
    }
  }
};

const withLocale = (targetUrl, locale) => {
  return targetUrl.replace('{{locale}}', locale);
};

const setUrlWithAvailableRuleSet = (ruleSets, ruleSetsRole, subMenu) => {
  const availableRuleSet = ruleSets?.filter(rule => ruleSetsRole?.includes(rule.name));

  if (!availableRuleSet) return subMenu;

  const targetUrl = `${subMenu.target_url?.split('?')[0]}?current_rule_set_name=${availableRuleSet[0]?.name}`;

  return {
    ...subMenu,
    target_url: targetUrl,
  };
};

const filterMenu = args => {
  const { currentRuleSetName, isAdmin, isAppSwitcher, locale, menu, organization, user } = args;
  const permissionManager = new PermissionManager(user, organization, currentRuleSetName);
  let hasAccess = true;

  if (!user?.all_access) {
    if (menu.all_access) {
      hasAccess = false;
    }
    if (hasAccess && menu.permissions) {
      hasAccess = permissionManager.hasAccess(menu.permissions, isAppSwitcher);

      if (hasItems(menu.target_url_array)) {
        menu.target_url = handleTargetUrlArrayValidation(menu.target_url_array, permissionManager);
      }
    }
  }

  if (hasAccess && menu) {
    if (menu.submenu_items?.length > 0) {
      menu.submenu_items = menu.submenu_items.filter(item =>
        filterMenu({ ...args, menu: item, isAppSwitcher: menu.id === 'app-switcher' })
      );
    }

    if (menu.title) {
      menu.title = replaceUserName(user, menu.title);
    }

    if (menu.id === 'school-picker-button') {
      menu.title = replaceSchoolName(user, menu.title);
      if (user?.schools?.length <= 1) {
        menu.target_url = replaceSchoolModalUrl(user, menu.target_url);
      }
    }

    if (menu.id === 'support_email') {
      menu.sub_text = replaceAccountManagerEmailText(user, menu.sub_text);
      menu.target_url = replaceAccountManagerEmailUrl(user, menu.target_url);
    }

    if (menu?.help_url_post_processing) {
      menu.target_url = replaceHelpUrl(menu, organization, permissionManager.isAdvisor());
    }

    if (menu?.privacy_processing) {
      menu.target_url = replacePrivacyUrl(menu, organization);
    }

    if (menu.id === 'app-switcher') {
      menu.submenu_items = menu.submenu_items.map(subMenu =>
        setUrlWithAvailableRuleSet(user.rule_sets, menuRoles[subMenu.id], subMenu)
      );
    }

    if (menu.id === 'my_profile') {
      const userIsManager = user?.is_manager || user?.['is_manager?'];
      menu.target_url = replaceMyProfileUrlContext(menu.target_url, userIsManager);
    }

    if (menu.id === 'localized') {
      menu.target_url = withLocale(menu.target_url, locale);
    }

    if (menu.target_url || menu.submenu_items?.length > 0) {
      if (menu.target_url) {
        if (dynamicSubdomainModules.includes(menu.module)) {
          menu.target_url = withDynamicSubdomain(menu.target_url, organization);
        }

        if (menu.target_url === 'language_modal') {
          return menu;
        }

        if (!isAdmin) {
          menu.target_url = replaceOrgSlug(menu, organization, user);
          menu.target_url = addCurrentRuleSetName(menu.target_url, currentRuleSetName);
        }
      }

      return menu;
    }
  }

  return null;
};

const isCurrentMenuItemPermitted = (menuData, currentUrl) => {
  if (!menuData) {
    return false;
  }

  const items = menuData.submenu_items.concat(menuData.submenu_items.flatMap(item => item.submenu_items));
  return !!items.find(item => item?.target_url?.includes(currentUrl));
};

const withPermissionIndicator = (menuData, currentUrl) => {
  return Object.assign({}, menuData, {
    current_menu_item_permitted: isCurrentMenuItemPermitted(menuData, currentUrl),
    permitted_whitelist: PERMITTED_WHITELIST,
  });
};

export const getMenuData = args => {
  const { slug, environment, currentUrl, service, ...props } = args;
  let menu = MENUS[slug] && MENUS[slug](environment, service);

  menu = filterMenu({ ...props, menu });

  if (slug === MENU_SLUG.customerAdminLeft) {
    menu = withPermissionIndicator(menu, currentUrl);
  }

  return menu;
};
